/* © NXAPP Limited */
import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { describeError } from '@/lib/describeError'
import {
  Building2, RefreshCw, UserPlus, Calendar, MapPin, Tag,
  Search, CheckCircle2, AlertCircle, X, Sparkles, Globe,
  Linkedin, Plus, Send, ChevronDown, ChevronUp, Link2,
  CheckSquare, Square, Zap, Filter,
} from 'lucide-react'
import { SIC_CODES, SIC_GROUPS, type SicCode } from './data/sicCodes'
import { authedFetch } from '@/lib/authedFetch'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Campaign { id: string; name: string; description: string | null; audience: 'director' | 'company' }
type SortDir = 'asc' | 'desc'
type SortCol = 'incorporation_date' | 'company_name' | 'registered_locality'

// ── Pipeline ──────────────────────────────────────────────────────────────────
// Shared "New" front tab, then splits into two tracks: Companies and Directors.
type Track = 'new' | 'companies' | 'directors'
type StageKey = 'directors' | 'enriched' | 'campaign' | 'master' | 'tolink'
type PipelineStage = 'new' | 'directors' | 'enriched' | 'campaign'

interface BulkProgress {
  action: 'directors' | 'enrich' | 'verify' | 'online' | 'findEmail'
  done: number
  total: number
  label: string        // current lead / company being processed
  startedAt: number    // ms epoch
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

function formatSecs(s: number): string {
  if (s < 60) return `${Math.round(s)}s`
  const m = Math.floor(s / 60)
  const r = Math.round(s % 60)
  return `${m}m ${r.toString().padStart(2, '0')}s`
}

interface Filters {
  dateRange: 'all' | 'yesterday' | 'this_week' | 'this_month' | 'last_30' | 'aged_1_3' | 'aged_3_10' | 'custom'
  customFrom: string; customTo: string
  sicCodes: string[]; location: string; companyName: string
  companyStatus: string; companyType: string
  directorFilter: 'all' | 'with' | 'without'
  campaignFilter: string   // '' = all, 'none' = unassigned, else campaign id
  verifyFilter: 'all' | LeadTier   // grey / red / orange / green / blue
}

interface OfficerData {
  id: string
  full_name: string
  role: string | null
  enriched: {
    id: string
    email: string | null
    email_confidence: 'high' | 'medium' | 'low' | null
    domain_verified: boolean | null
    online_verified: boolean | null
    company_email: string | null
    linkedin_url: string | null
    twitter_url: string | null
    facebook_url: string | null
    instagram_url: string | null
    company_website: string | null
    enrichment_status: string
    enriched_at: string | null
    email_source: string | null
    email_source_detail: string | null
    email_searched_at: string | null
    email_search_result: string | null
  } | null
  campaigns: { id: string; name: string }[]
}

interface CompanyData {
  id: string
  company_number: string
  company_name: string
  company_status: string | null
  company_type: string | null
  incorporation_date: string | null
  sic_codes: string[]
  registered_locality: string | null
  ch_officers: unknown[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateRange(range: Filters['dateRange'], from: string, to: string) {
  const today = new Date()
  const fmt = (d: Date) => d.toISOString().split('T')[0]
  switch (range) {
    case 'yesterday': { const y = new Date(today); y.setDate(today.getDate() - 1); return { from: fmt(y), to: fmt(y) } }
    case 'this_week': { const m = new Date(today); m.setDate(today.getDate() - today.getDay() + 1); return { from: fmt(m), to: fmt(today) } }
    case 'this_month': return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) }
    case 'last_30': { const d = new Date(today); d.setDate(today.getDate() - 30); return { from: fmt(d), to: fmt(today) } }
    // Established companies — old enough to have a website, so enrichment can
    // actually find a domain. Brand-new incorporations have no web presence.
    case 'aged_1_3': {
      const to3 = new Date(today); to3.setFullYear(today.getFullYear() - 1)
      const from3 = new Date(today); from3.setFullYear(today.getFullYear() - 3)
      return { from: fmt(from3), to: fmt(to3) }
    }
    case 'aged_3_10': {
      const to10 = new Date(today); to10.setFullYear(today.getFullYear() - 3)
      const from10 = new Date(today); from10.setFullYear(today.getFullYear() - 10)
      return { from: fmt(from10), to: fmt(to10) }
    }
    case 'custom': return { from, to }
    case 'all': return { from: '', to: '' }
    default: return { from: '', to: '' }
  }
}

// Lead quality ladder: grey (unchecked) · red (no mail server) · orange · green · blue (top)
//
// IMPORTANT: none of these tiers mean the MAILBOX exists. The domain is found by
// web search and MX-checked; the local part is a name pattern. Only 'blue' adds
// evidence that the person is real. Treat every address as unconfirmed until it
// has actually accepted a delivery.
type LeadTier = 'grey' | 'red' | 'orange' | 'green' | 'blue'
function leadTier(e: OfficerData['enriched']): LeadTier | null {
  if (!e?.email) return null
  if (e.online_verified === true) return 'blue'          // confirmed online (top)
  if (e.domain_verified === true)                        // deliverable domain
    return e.email_confidence === 'high' ? 'green' : 'orange'
  if (e.domain_verified === false) return 'red'          // confirmed no mail server
  return 'grey'                                          // not checked yet
}

// The company's generic inbox (info@) — same across all its directors, so take the first.
function companyInbox(officers: OfficerData[]): string | null {
  return officers.find(o => o.enriched?.company_email)?.enriched?.company_email ?? null
}

function normaliseOfficers(company: CompanyData): OfficerData[] {
  return ((company.ch_officers ?? []) as Record<string, unknown>[]).map(o => {
    const raw = o.officer_enrichment
    const enr = raw ? ((Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown>) : null
    const campaigns = ((o.lead_campaign_assignments ?? []) as Record<string, unknown>[])
      .map(a => a.campaigns as { id: string; name: string } | null)
      .filter((c): c is { id: string; name: string } => !!c)
    return {
      id: o.id as string,
      full_name: o.full_name as string,
      role: o.role as string | null,
      enriched: enr ? {
        id: enr.id as string,
        email: enr.email as string | null,
        email_confidence: enr.email_confidence as 'high' | 'medium' | 'low' | null,
        domain_verified: (enr.domain_verified ?? null) as boolean | null,
        online_verified: (enr.online_verified ?? null) as boolean | null,
        company_email: (enr.company_email ?? null) as string | null,
        linkedin_url: enr.linkedin_url as string | null,
        twitter_url: enr.twitter_url as string | null,
        facebook_url: enr.facebook_url as string | null,
        instagram_url: enr.instagram_url as string | null,
        company_website: enr.company_website as string | null,
        enrichment_status: (enr.enrichment_status as string) ?? 'pending',
        enriched_at: (enr.enriched_at as string) ?? null,
        email_source: (enr.email_source as string) ?? null,
        email_source_detail: (enr.email_source_detail as string) ?? null,
        email_searched_at: (enr.email_searched_at as string) ?? null,
        email_search_result: (enr.email_search_result as string) ?? null,
      } : null,
      campaigns,
    }
  })
}

// Director-track stage: furthest-progressed director. "In Campaign" here means a
// director's personal email is in a DIRECTOR campaign (company inbox is a separate track).
function directorStage(company: CompanyData): PipelineStage {
  const officers = normaliseOfficers(company)
  if (officers.length === 0) return 'new'
  if (officers.some(o => o.campaigns.length > 0)) return 'campaign'
  if (officers.some(o => o.enriched?.email)) return 'enriched'
  return 'directors'
}

// Directors track — companies that already have directors (post "New")
const DIRECTOR_STAGES: { key: StageKey; label: string; hint: string }[] = [
  { key: 'directors', label: 'Has Director', hint: 'Directors fetched — not enriched' },
  { key: 'enriched',  label: 'Enriched',     hint: 'Email found — not in a director campaign' },
  { key: 'campaign',  label: 'In Campaign',  hint: 'Director in a director campaign' },
  { key: 'master',    label: 'Master',       hint: 'All companies with directors' },
]

// Company-inbox track: only companies with a verified info@ inbox.
const COMPANY_STAGES: { key: StageKey; label: string; hint: string }[] = [
  { key: 'tolink',   label: 'To link',     hint: 'Verified inbox — not in a company campaign' },
  { key: 'campaign', label: 'In Campaign', hint: 'Inbox in a company campaign' },
  { key: 'master',   label: 'Master',      hint: 'All companies with an inbox' },
]

// ── LeadsPage ─────────────────────────────────────────────────────────────────

export default function LeadsPage() {
  const qc = useQueryClient()
  const [filters, setFilters] = useState<Filters>({
    dateRange: 'yesterday', customFrom: '', customTo: '',
    sicCodes: [], location: '', companyName: '', companyStatus: 'active', companyType: 'ltd',
    directorFilter: 'all', campaignFilter: '', verifyFilter: 'all',
  })
  const [showSicPicker, setShowSicPicker] = useState(false)
  const [sicSearch, setSicSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(1000)
  const [sortCol, setSortCol] = useState<SortCol>('incorporation_date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [syncResult, setSyncResult] = useState<{ imported: number; totalAvailable: number } | null>(null)
  const [showBackfillModal, setShowBackfillModal] = useState(false)
  const [syncMax, setSyncMax] = useState(500)
  const [stage, setStage] = useState<StageKey>('directors')
  const [track, setTrack] = useState<Track>('new')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set())
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null)
  const [, setTick] = useState(0)   // drives live ETA re-render while a bulk run is active
  const bulkStopRef = useRef(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(new Set())
  const [selectedInboxes, setSelectedInboxes] = useState<Set<string>>(new Set())  // company ids (info@ inbox)
  const [modalTarget, setModalTarget] = useState<'director' | 'company'>('director')  // which audience the modal is linking
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(new Set())
  const [newSegmentName, setNewSegmentName] = useState('')
  const [newCampaignName, setNewCampaignName] = useState('')
  const [addResult, setAddResult] = useState<{ added: number; skipped: number } | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  useEffect(() => {
    supabase.auth.getUser().then(r => setUserId(r.data.user?.id ?? null))
  }, [])

  // Tick every second while a bulk run is active so elapsed/ETA stay live
  useEffect(() => {
    if (!bulkProgress) return
    const t = setInterval(() => setTick(n => n + 1), 1000)
    return () => clearInterval(t)
  }, [bulkProgress])

  // ── Main data query ──
  // Fetch ALL matching companies in 1000-row chunks (PostgREST caps a single
  // request at 1000). Stage classification, per-stage counts and pagination are
  // then all done client-side over the complete set — otherwise stages beyond
  // the first page (e.g. "In Campaign") would show 0.
  const CHUNK = 1000
  const FETCH_CAP = 8000
  const { data, isLoading, error: leadsError } = useQuery({
    queryKey: ['leads', sortCol, sortDir,
      filters.companyStatus, filters.companyType, filters.location, filters.companyName, filters.sicCodes],
    queryFn: async () => {
      const all: CompanyData[] = []
      for (let start = 0; start < FETCH_CAP; start += CHUNK) {
        let q = supabase
          .from('ch_companies')
          .select(`
            id, company_number, company_name, company_status, company_type,
            incorporation_date, sic_codes, registered_locality,
            ch_officers (
              id, full_name, role,
              officer_enrichment ( id, email, email_confidence, domain_verified, online_verified, company_email, linkedin_url,
                twitter_url, facebook_url, instagram_url, company_website, enrichment_status,
                enriched_at, email_source, email_source_detail,
                email_searched_at, email_search_result ),
              lead_campaign_assignments (
                campaign_id, campaigns ( id, name )
              )
            )
          `)
          .order(sortCol, { ascending: sortDir === 'asc' })
          .range(start, start + CHUNK - 1)

        if (filters.companyStatus) q = q.eq('company_status', filters.companyStatus)
        if (filters.companyType)   q = q.eq('company_type',   filters.companyType)
        if (filters.location)      q = q.ilike('registered_locality', `%${filters.location}%`)
        if (filters.companyName)   q = q.ilike('company_name', `%${filters.companyName}%`)
        if (filters.sicCodes.length) q = q.overlaps('sic_codes', filters.sicCodes)

        const { data, error } = await q
        if (error) throw error
        const rows = (data ?? []) as unknown as CompanyData[]
        all.push(...rows)
        if (rows.length < CHUNK) break
      }
      return { rows: all, count: all.length }
    },
  })

  // ── Campaigns list — explicit user_id filter so it works regardless of RLS setup ──
  const { data: campaigns, isLoading: campaignsLoading, error: campaignsError, refetch: refetchCampaigns } = useQuery({
    queryKey: ['campaigns_list', userId],
    enabled: !!userId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, description, audience')
        .eq('user_id', userId!)
        .order('name')
      if (error) throw error
      return (data ?? []) as Campaign[]
    },
  })

  // ── Segments (for direct link-to-segment) ──
  const { data: segmentsList, refetch: refetchSegments } = useQuery({
    queryKey: ['segments_list_leads', userId],
    enabled: !!userId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('segments').select('id, name')
        .eq('user_id', userId!).order('name')
      if (error) throw error
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  // ── Company-inbox campaign memberships (officer_id null → not on any officer row) ──
  const { data: inboxCampaignMap } = useQuery({
    queryKey: ['inbox_campaigns', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('lead_campaign_assignments')
        .select('company_id, campaigns ( id, name )')
        .eq('user_id', userId!)
        .eq('target', 'company')
      const m = new Map<string, { id: string; name: string }[]>()
      for (const r of (data ?? []) as Record<string, unknown>[]) {
        const cid = r.company_id as string | null
        const camp = r.campaigns as { id: string; name: string } | null
        if (!cid || !camp) continue
        const arr = m.get(cid) ?? []
        arr.push(camp); m.set(cid, arr)
      }
      return m
    },
  })
  const inboxCompanyIds = useMemo(() => new Set(inboxCampaignMap?.keys() ?? []), [inboxCampaignMap])

  // ── Mutations ──
  const syncMut = useMutation({
    mutationFn: async () => {
      const { from, to } = getDateRange(filters.dateRange, filters.customFrom, filters.customTo)
      const res = await authedFetch('/api/leads/sync-companies', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          incorporatedFrom: from || undefined, incorporatedTo: to || undefined,
          sicCodes: filters.sicCodes.length ? filters.sicCodes : undefined,
          location: filters.location || undefined,
          companyStatus: filters.companyStatus || undefined,
          companyType: filters.companyType || undefined,
          maxResults: syncMax,
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<{ imported: number; totalAvailable: number }>
    },
    onSuccess: r => { setSyncResult(r); setShowBackfillModal(false); qc.invalidateQueries({ queryKey: ['leads'] }) },
  })

  const dirMut = useMutation({
    mutationFn: async (companyId: string) => {
      const res = await authedFetch('/api/leads/get-officers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<{ officers: number; new: number }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })

  const enrichMut = useMutation({
    mutationFn: async (officerId: string) => {
      const res = await authedFetch('/api/leads/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ officerId }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })

  const addMut = useMutation({
    mutationFn: async () => {
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      const res = await authedFetch('/api/leads/add-to-campaigns', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Scope to the modal's audience so a director email can never land in a company campaign
          officerIds: modalTarget === 'director'
            ? Array.from(selected).filter(id => officerIndex.get(id)?.officer.enriched?.domain_verified === true)
            : [],
          companyInboxIds: modalTarget === 'company' ? Array.from(selectedInboxes) : [],
          campaignIds: Array.from(selectedCampaigns),
          segmentIds: Array.from(selectedSegments),
          userId: uid,
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<{ added: number; skipped: number }>
    },
    onSuccess: r => {
      setAddResult(r); setShowModal(false)
      clearSelection(); setSelectedCampaigns(new Set()); setSelectedSegments(new Set())
      qc.invalidateQueries({ queryKey: ['leads'] })
      qc.invalidateQueries({ queryKey: ['inbox_campaigns'] })
    },
  })

  const createSegMut = useMutation({
    mutationFn: async () => {
      if (!newSegmentName.trim()) throw new Error('Segment name required')
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('segments')
        .insert({ name: newSegmentName.trim(), user_id: uid })
        .select('id').single()
      if (error) throw error
      return data
    },
    onSuccess: d => {
      setSelectedSegments(prev => new Set([...prev, d.id]))
      setNewSegmentName('')
      refetchSegments()
    },
  })

  const createMut = useMutation({
    mutationFn: async () => {
      if (!newCampaignName.trim()) throw new Error('Campaign name required')
      const uid = userId ?? (await supabase.auth.getUser()).data.user?.id
      if (!uid) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('campaigns')
        .insert({ name: newCampaignName.trim(), user_id: uid, audience: modalTarget })
        .select('id').single()
      if (error) throw error
      return data
    },
    onSuccess: d => {
      setSelectedCampaigns(prev => new Set([...prev, d.id]))
      setNewCampaignName('')
      refetchCampaigns()
    },
  })

  // ── Derived state ──
  // The Companies track uses the inbox layout; New + Directors use the director layout.
  const addressView: 'directors' | 'inboxes' = track === 'companies' ? 'inboxes' : 'directors'

  // Top-level track counts (shown on the 3 track buttons)
  const trackCounts = useMemo(() => {
    const rows = data?.rows ?? []
    let neu = 0, dir = 0, co = 0
    for (const c of rows) {
      if (directorStage(c) === 'new') neu++; else dir++
      if (companyInbox(normaliseOfficers(c)) !== null) co++
    }
    return { new: neu, directors: dir, companies: co }
  }, [data?.rows])

  // Sub-stage counts for the active track
  const stageCounts = useMemo(() => {
    const rows = data?.rows ?? []
    if (track === 'companies') {
      const c: Record<string, number> = { tolink: 0, campaign: 0, master: 0 }
      for (const co of rows) {
        if (companyInbox(normaliseOfficers(co)) === null) continue
        c.master++
        if (inboxCompanyIds.has(co.id)) c.campaign++; else c.tolink++
      }
      return c
    }
    // Directors track (companies that already have directors)
    const c: Record<string, number> = { directors: 0, enriched: 0, campaign: 0, master: 0 }
    for (const co of rows) {
      const s = directorStage(co)
      if (s === 'new') continue
      c[s]++; c.master++
    }
    return c
  }, [data?.rows, track, inboxCompanyIds])

  const displayedRows = useMemo(() => {
    let rows = data?.rows ?? []

    if (track === 'new') {
      // New: freshly synced companies with no directors fetched yet
      return rows.filter(c => directorStage(c) === 'new')
    }

    if (track === 'companies') {
      rows = rows.filter(c => companyInbox(normaliseOfficers(c)) !== null)
      if (stage === 'tolink')        rows = rows.filter(c => !inboxCompanyIds.has(c.id))
      else if (stage === 'campaign') rows = rows.filter(c => inboxCompanyIds.has(c.id))
      return rows
    }

    // Directors track — only companies that have directors
    rows = rows.filter(c => directorStage(c) !== 'new')
    if (stage !== 'master') rows = rows.filter(c => directorStage(c) === stage)
    if (filters.directorFilter === 'without') rows = rows.filter(c => (c.ch_officers ?? []).length === 0)
    if (filters.campaignFilter === 'none') {
      rows = rows.filter(c => normaliseOfficers(c).every(o => o.campaigns.length === 0))
    } else if (filters.campaignFilter) {
      rows = rows.filter(c => normaliseOfficers(c).some(o => o.campaigns.some(camp => camp.id === filters.campaignFilter)))
    }
    if (filters.verifyFilter !== 'all') {
      rows = rows.filter(c => normaliseOfficers(c).some(o => leadTier(o.enriched) === filters.verifyFilter))
    }
    return rows
  }, [data?.rows, stage, track, inboxCompanyIds, filters.directorFilter, filters.campaignFilter, filters.verifyFilter])

  // Client-side pagination over the stage-filtered set
  const pagedRows = useMemo(
    () => displayedRows.slice(page * pageSize, (page + 1) * pageSize),
    [displayedRows, page, pageSize])

  // Clamp page if the filtered set shrinks below the current page window
  useEffect(() => {
    if (page > 0 && page * pageSize >= displayedRows.length) setPage(0)
  }, [displayedRows.length, page, pageSize])

  // NOTE: derived from displayedRows (every row matching the current filters),
  // NOT from pagedRows. Named accordingly — the old name said "OnPage" and meant
  // the opposite, which made Select All's scope impossible to reason about.
  const allOfficersFiltered = useMemo((): OfficerData[] =>
    displayedRows.flatMap(c => normaliseOfficers(c)), [displayedRows])

  const officersOnPage = useMemo((): OfficerData[] =>
    pagedRows.flatMap(c => normaliseOfficers(c)), [pagedRows])

  // Lookups for cost-saving skip logic (bulk actions never re-hit the API for
  // work already done — only the per-row Refresh / Re-enrich buttons force it)
  const companyById = useMemo(() => {
    const m = new Map<string, CompanyData>()
    for (const c of data?.rows ?? []) m.set(c.id, c)
    return m
  }, [data?.rows])
  const officerIndex = useMemo(() => {
    const m = new Map<string, { officer: OfficerData; company: string }>()
    for (const c of data?.rows ?? []) for (const o of normaliseOfficers(c)) m.set(o.id, { officer: o, company: c.company_name })
    return m
  }, [data?.rows])

  const enrichableOfficers = allOfficersFiltered.filter(o =>
    !o.enriched?.email && o.enriched?.enrichment_status !== 'running')

  // Directors needing an MX check — never checked, OR verified but missing the
  // generic company email (backfills company_email for pre-existing verified rows)
  const unverifiedOfficers = useMemo(() =>
    (data?.rows ?? []).flatMap(c => normaliseOfficers(c))
      .filter(o => o.enriched?.email && (
        o.enriched.domain_verified === null ||
        (o.enriched.domain_verified === true && !o.enriched.company_email)
      )),
    [data?.rows])

  // Light-green directors (domain OK) not yet deep-verified online (dark-green target)
  const deepVerifyableOfficers = useMemo(() =>
    (data?.rows ?? []).flatMap(c => normaliseOfficers(c))
      .filter(o => o.enriched?.domain_verified === true && o.enriched.online_verified === null),
    [data?.rows])

  // Select All, per track:
  //  New → companies (for Get Directors) · Companies → every inbox · Directors → directors
  // scope 'page'   → only the rows currently visible
  // scope 'all'     → every row matching the active filters, across all pages
  const selectAll = (scope: 'page' | 'all' = 'all') => {
    const rows = scope === 'page' ? pagedRows : displayedRows
    const officers = scope === 'page' ? officersOnPage : allOfficersFiltered
    if (track === 'new') {
      setSelectedCompanies(new Set(rows.map(c => c.id)))
    } else if (track === 'companies') {
      setSelectedInboxes(new Set(rows.map(c => c.id)))
    } else if (stage === 'directors') {
      setSelected(new Set(officers.map(o => o.id)))   // enrich: all directors
    } else {
      setSelected(new Set(officers.filter(o => o.enriched?.domain_verified === true).map(o => o.id)))  // link: deliverable only
    }
  }

  // One definition, rendered above and below the table — long pages are
  // unusable if the only pager is 1000 rows down.
  const paginationBar = (pos: 'top' | 'bottom') => (
    <div className={`flex items-center gap-3 px-4 py-2 ${pos === 'top' ? 'border-b' : 'border-t'} border-gray-100 bg-gray-50 text-[11px] text-gray-500 flex-wrap`}>
      <span className="font-medium">{displayedRows.length === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, displayedRows.length)}</span>
      <span>of {displayedRows.length.toLocaleString()} companies</span>
      {selected.size > 0 && <span className="text-brand-600 font-medium">· {selected.size} selected</span>}
      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={() => qc.refetchQueries({ queryKey: ['leads'], type: 'active' })}
          className="flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-white hover:bg-gray-50 text-[11px] text-gray-600"
          title="Reload the table from the database"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        <span className="text-gray-400">Show</span>
        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0) }}
          className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] bg-white text-gray-700">
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={250}>250</option>
          <option value={1000}>1000</option>
        </select>
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
          className="px-2 py-0.5 rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-30 text-[11px]">←</button>
        <span className="text-gray-400">p.{page + 1}/{Math.max(1, Math.ceil(displayedRows.length / pageSize))}</span>
        <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * pageSize >= displayedRows.length}
          className="px-2 py-0.5 rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-30 text-[11px]">→</button>
      </div>
    </div>
  )

  const clearSelection = () => { setSelected(new Set()); setSelectedCompanies(new Set()); setSelectedInboxes(new Set()) }

  // Shared runner for every bulk action — drives the live progress banner and
  // runs `concurrency` items at once with retry+backoff on rate limits (429/5xx).
  const processQueue = async (
    action: BulkProgress['action'],
    endpoint: string,
    bodyKey: 'companyId' | 'officerId',
    items: { id: string; label: string }[],
    opts: { concurrency: number; clearSel?: boolean },
  ) => {
    bulkStopRef.current = false
    setBulkProgress({ action, done: 0, total: items.length, label: '', startedAt: Date.now() })

    const postWithRetry = async (id: string) => {
      for (let attempt = 0; attempt < 4; attempt++) {
        if (bulkStopRef.current) return
        try {
          const res = await authedFetch(endpoint, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [bodyKey]: id }),
          })
          if (res.status === 429 || res.status >= 500) {   // throttled / transient — back off & retry
            await sleep(600 * 2 ** attempt)
            continue
          }
          return
        } catch {
          await sleep(600 * 2 ** attempt)   // network blip — retry
        }
      }
    }

    let completed = 0
    let next = 0
    const worker = async () => {
      while (!bulkStopRef.current) {
        const i = next++
        if (i >= items.length) return
        setBulkProgress(p => p && { ...p, label: items[i].label })
        await postWithRetry(items[i].id)
        completed++
        setBulkProgress(p => p && { ...p, done: completed })
        // Pull results in as they land rather than only at the end — a long run
        // otherwise looks like nothing is happening until it finishes.
        if (completed % 10 === 0) qc.refetchQueries({ queryKey: ['leads'], type: 'active' })
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(opts.concurrency, items.length) }, () => worker()))

    if (opts.clearSel) clearSelection()
    // Awaited and forced: invalidate alone marks the query stale, which with a
    // 30s staleTime could leave the old rows on screen until the next fetch.
    setBulkProgress(p => p && { ...p, label: 'Refreshing results…' })
    await qc.refetchQueries({ queryKey: ['leads'], type: 'active' })
    setBulkProgress(null)
  }

  // Stage 1 → 2 : fetch directors — SKIP companies that already have directors
  const getDirectorsForSelected = () => {
    const items = Array.from(selectedCompanies)
      .map(id => companyById.get(id))
      .filter((c): c is CompanyData => !!c && normaliseOfficers(c).length === 0)
      .map(c => ({ id: c.id, label: c.company_name }))
    return processQueue('directors', '/api/leads/get-officers', 'companyId', items, { concurrency: 1, clearSel: true })
  }

  // Stage 2 → 3 : enrich — SKIP directors already enriched (saves AI API cost)
  const enrichSelected = () => {
    const items = Array.from(selected)
      .map(id => officerIndex.get(id))
      .filter((x): x is { officer: OfficerData; company: string } => !!x && !x.officer.enriched?.email)
      .map(x => ({ id: x.officer.id, label: `${x.officer.full_name} · ${x.company}` }))
    return processQueue('enrich', '/api/leads/enrich', 'officerId', items, { concurrency: 3, clearSel: true })
  }

  // Master "Enrich All" — every un-enriched director in the current view
  const enrichAll = () => {
    const items = enrichableOfficers.map(o => {
      const meta = officerIndex.get(o.id)
      return { id: o.id, label: meta ? `${o.full_name} · ${meta.company}` : o.full_name }
    })
    return processQueue('enrich', '/api/leads/enrich', 'officerId', items, { concurrency: 3 })
  }

  // Verify domains — free MX check over emails not yet graded (no AI cost).
  // DNS is cheap so run 8 at a time.
  const verifyDomains = () => {
    const items = unverifiedOfficers.map(o => {
      const meta = officerIndex.get(o.id)
      return { id: o.id, label: meta ? `${o.full_name} · ${meta.company}` : o.full_name }
    })
    return processQueue('verify', '/api/leads/verify-domain', 'officerId', items, { concurrency: 8 })
  }

  // Deep verify online — AI + web search over light-green leads (dark-green upgrade).
  // Heavier + costs money, so 3 at a time.
  const verifyOnline = () => {
    const items = deepVerifyableOfficers.map(o => {
      const meta = officerIndex.get(o.id)
      return { id: o.id, label: meta ? `${o.full_name} · ${meta.company}` : o.full_name }
    })
    return processQueue('online', '/api/leads/verify-online', 'officerId', items, { concurrency: 3 })
  }

  // Search the web for the director's ACTUAL published address. Most expensive
  // per lead and often finds nothing, so it runs on the current selection only.
  const findEmailsForSelected = () => {
    const items = [...selected].map(id => {
      const meta = officerIndex.get(id)
      return { id, label: meta ? `${meta.officer.full_name} · ${meta.company}` : id }
    })
    return processQueue('findEmail', '/api/leads/find-email', 'officerId', items, { concurrency: 2 })
  }

  // Master "Get missing directors" — only companies that don't have any yet
  const refreshAll = () => {
    const items = (data?.rows ?? [])
      .filter(c => normaliseOfficers(c).length === 0)
      .map(c => ({ id: c.id, label: c.company_name }))
    return processQueue('directors', '/api/leads/get-officers', 'companyId', items, { concurrency: 1 })
  }

  // Directors are tickable-for-campaign only when deliverable (enriched/campaign/master
  // stages of the Directors track). In the "Has Director" stage they're tickable for enrich.
  const emailSelectMode = track === 'companies' || (track === 'directors' && stage !== 'directors')

  const openCampaignModal = (officerIds: string[]) => {
    setSelected(prev => new Set([...prev, ...officerIds]))
    setModalTarget('director'); refetchCampaigns(); setShowModal(true)
  }
  const openInboxModal = (companyId: string) => {
    setSelectedInboxes(prev => new Set([...prev, companyId]))
    setModalTarget('company'); refetchCampaigns(); setShowModal(true)
  }
  // Bulk "Link → Campaign" from the toolbar — target follows the current address view
  const openBulkModal = () => {
    setModalTarget(addressView === 'inboxes' ? 'company' : 'director'); refetchCampaigns(); setShowModal(true)
  }

  const toggleDirector = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleInbox = (companyId: string) =>
    setSelectedInboxes(prev => { const n = new Set(prev); n.has(companyId) ? n.delete(companyId) : n.add(companyId); return n })

  // Company checkbox. Email stages → tick this company's deliverable personal emails.
  // Early stages → select the company + all directors (for Get Directors / Enrich).
  const toggleCompany = (company: CompanyData, officers: OfficerData[]) => {
    if (emailSelectMode) {
      const ids = officers.filter(o => o.enriched?.domain_verified === true).map(o => o.id)
      if (!ids.length) return
      const allOn = ids.every(id => selected.has(id))
      setSelected(prev => { const n = new Set(prev); ids.forEach(id => allOn ? n.delete(id) : n.add(id)); return n })
      return
    }
    const on = selectedCompanies.has(company.id)
    setSelectedCompanies(prev => { const n = new Set(prev); on ? n.delete(company.id) : n.add(company.id); return n })
    const ids = officers.map(o => o.id)
    setSelected(prev => { const n = new Set(prev); ids.forEach(id => on ? n.delete(id) : n.add(id)); return n })
  }

  const changeStage = (s: StageKey) => { setStage(s); clearSelection(); setPage(0) }
  // Switch track — reset to that track's first stage
  const changeTrack = (t: Track) => { setTrack(t); setStage(t === 'companies' ? 'tolink' : 'directors'); clearSelection(); setPage(0) }
  const trackStages = track === 'companies' ? COMPANY_STAGES : DIRECTOR_STAGES

  const sortToggle = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
    setPage(0)
  }

  // ── SIC picker ──
  const filteredSics = useMemo((): Record<string, SicCode[]> => {
    const q = sicSearch.toLowerCase()
    const filtered = q ? SIC_CODES.filter(s => s.code.includes(q) || s.label.toLowerCase().includes(q)) : SIC_CODES
    return SIC_GROUPS.reduce((acc, g) => {
      const codes = filtered.filter(s => s.group === g)
      if (codes.length) acc[g] = codes
      return acc
    }, {} as Record<string, SicCode[]>)
  }, [sicSearch])

  const toggleSic = (code: string) =>
    setFilters(f => ({ ...f, sicCodes: f.sicCodes.includes(code) ? f.sicCodes.filter(c => c !== code) : [...f.sicCodes, code] }))

  const dateButtons: { key: Filters['dateRange']; label: string }[] = [
    { key: 'all', label: 'All time' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This week' },
    { key: 'this_month', label: 'This month' },
    { key: 'last_30', label: 'Last 30 days' },
    { key: 'aged_1_3', label: '1–3 yrs old' },
    { key: 'aged_3_10', label: '3–10 yrs old' },
    { key: 'custom', label: 'Custom' },
  ]

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Engine</h1>
          <p className="text-xs text-gray-500 mt-0.5">UK Companies House · director contacts · campaign targeting</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <button onClick={() => setShowBackfillModal(true)} disabled={syncMut.isPending}
              className="btn-secondary flex items-center gap-2 text-xs">
              <RefreshCw className={`w-3.5 h-3.5 ${syncMut.isPending ? 'animate-spin' : ''}`} />
              {syncMut.isPending ? 'Importing…' : 'Backfill from Companies House'}
            </button>
            <p className="text-[10px] text-gray-400 mt-0.5 text-right">Cron auto-syncs daily at 06:00 UTC</p>
          </div>
        </div>
      </div>

      {/* Bulk progress banner — live count, current lead, progress bar, ETA */}
      {bulkProgress && (() => {
        const { action, done, total, label, startedAt } = bulkProgress
        const pct = total ? Math.round((done / total) * 100) : 0
        const elapsed = (Date.now() - startedAt) / 1000
        const rate = done > 0 ? elapsed / done : 0            // secs per lead
        const remaining = done > 0 ? rate * (total - done) : null
        const theme = action === 'enrich'
          ? { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', icon: 'text-amber-600', bar: 'bg-amber-500', verb: 'Enriching', suffix: ' · 3 at a time' }
          : action === 'verify'
          ? { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', icon: 'text-emerald-600', bar: 'bg-emerald-500', verb: 'Verifying domains', suffix: ' · 8 at a time' }
          : action === 'online'
          ? { bg: 'bg-green-100 border-green-300', text: 'text-green-900', icon: 'text-green-700', bar: 'bg-green-700', verb: 'Deep-verifying online', suffix: ' · 3 at a time' }
          : action === 'findEmail'
          ? { bg: 'bg-blue-50 border-blue-300', text: 'text-blue-900', icon: 'text-blue-700', bar: 'bg-blue-600', verb: 'Searching the web for real emails', suffix: ' · 2 at a time · many will not be found' }
          : { bg: 'bg-brand-50 border-brand-200', text: 'text-brand-800', icon: 'text-brand-600', bar: 'bg-brand-500', verb: 'Getting directors for', suffix: '' }
        return (
          <div className={`rounded-lg border px-4 py-3 ${theme.bg}`}>
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className={`w-3.5 h-3.5 shrink-0 animate-spin ${theme.icon}`} />
              <span className={`text-xs font-semibold ${theme.text}`}>
                {theme.verb} {done} of {total} done{theme.suffix}
              </span>
              <span className="text-[11px] text-gray-500 truncate">{label || '…'}</span>
              <div className="ml-auto flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-gray-500">
                  {remaining !== null
                    ? <>~{formatSecs(remaining)} left · {formatSecs(elapsed)} elapsed</>
                    : <>estimating…</>}
                </span>
                <button onClick={() => { bulkStopRef.current = true }}
                  className="text-[11px] font-medium text-red-600 hover:text-red-800 border border-red-200 bg-white rounded px-2 py-0.5">
                  Stop
                </button>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-300 ${theme.bar}`}
                style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              {pct}% · {rate > 0 ? `~${rate.toFixed(1)}s per lead` : 'measuring speed…'} · leaving this page stops the run
            </p>
          </div>
        )
      })()}

      {leadsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-xs font-semibold text-red-800">Could not load leads</p>
          <p className="mt-0.5 text-xs text-red-700 font-mono break-all">{describeError(leadsError)}</p>
          {/(does not exist|schema cache|PGRST\d+)/i.test(describeError(leadsError)) && (
            <p className="mt-1.5 text-xs text-red-700">
              A column is missing — run any unapplied files in <code>supabase/migrations/</code>
              in the Supabase SQL editor, then <code>notify pgrst, 'reload schema';</code>.
              The table shows empty rather than erroring without this message.
            </p>
          )}
        </div>
      )}

      {/* Banners */}
      {addResult && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2.5 text-xs text-green-700 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Added <strong className="mx-0.5">{addResult.added}</strong> director{addResult.added !== 1 ? 's' : ''} to campaigns.
          {addResult.skipped > 0 && <span className="opacity-70">({addResult.skipped} skipped — no email or unverified domain)</span>}
          <button onClick={() => setAddResult(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {syncResult && !syncMut.isPending && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-2.5 text-xs text-blue-700 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Synced <strong className="mx-0.5">{syncResult.imported}</strong> companies (of {syncResult.totalAvailable.toLocaleString()} matching).
          <button onClick={() => setSyncResult(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {syncMut.isError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-xs text-red-700 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {syncMut.error instanceof Error ? syncMut.error.message : 'Sync failed'}
          <button onClick={() => syncMut.reset()} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Filter card — view filters only (import filters live in the Backfill modal) */}
      <div className="card">
        <div className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-gray-400 shrink-0">
            <Filter className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium">View</span>
          </div>
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
            <Search className="w-3 h-3 text-gray-400" />
            <input type="text" placeholder="Company name" value={filters.companyName}
              onChange={e => { setFilters(f => ({ ...f, companyName: e.target.value })); setPage(0) }}
              className="outline-none text-[11px] text-gray-700 placeholder-gray-400 w-32 bg-transparent" />
          </div>
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
            <MapPin className="w-3 h-3 text-gray-400" />
            <input type="text" placeholder="Location" value={filters.location}
              onChange={e => { setFilters(f => ({ ...f, location: e.target.value })); setPage(0) }}
              className="outline-none text-[11px] text-gray-700 placeholder-gray-400 w-24 bg-transparent" />
          </div>
          <select value={filters.companyStatus} onChange={e => { setFilters(f => ({ ...f, companyStatus: e.target.value })); setPage(0) }} className="input py-1.5 text-[11px]">
            <option value="active">Active</option>
            <option value="">All statuses</option>
            <option value="dissolved">Dissolved</option>
          </select>
          <select value={filters.companyType} onChange={e => { setFilters(f => ({ ...f, companyType: e.target.value })); setPage(0) }} className="input py-1.5 text-[11px]">
            <option value="ltd">Ltd</option>
            <option value="">All types</option>
            <option value="llp">LLP</option>
            <option value="plc">PLC</option>
          </select>
          <button onClick={() => setShowSicPicker(v => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg border font-medium transition-colors ${filters.sicCodes.length ? 'border-brand-500 text-brand-600 bg-brand-50' : 'border-gray-200 text-gray-600 bg-white hover:border-brand-400'}`}>
            <Tag className="w-3 h-3" />
            SIC{filters.sicCodes.length ? ` (${filters.sicCodes.length})` : ''}
            {showSicPicker ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
          </button>

          <div className="w-px h-4 bg-gray-200 shrink-0" />

          {/* Director presence filter */}
          <div className="flex items-center gap-1 border border-gray-200 rounded-lg bg-white overflow-hidden">
            {(['all', 'with', 'without'] as const).map(v => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, directorFilter: v }))}
                className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${filters.directorFilter === v ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {v === 'all' ? 'All' : v === 'with' ? 'Has Directors' : 'No Directors'}
              </button>
            ))}
          </div>

          {/* Campaign filter */}
          <div className="flex items-center gap-1.5">
            <Filter className="w-3 h-3 text-gray-400 shrink-0" />
            <select value={filters.campaignFilter}
              onChange={e => setFilters(f => ({ ...f, campaignFilter: e.target.value }))}
              className={`border rounded-lg px-2 py-1.5 text-[11px] bg-white transition-colors ${filters.campaignFilter ? 'border-brand-400 text-brand-600' : 'border-gray-200 text-gray-600'}`}>
              <option value="">All campaigns</option>
              <option value="none">Not in any campaign</option>
              {campaigns?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Lead quality filter — blue / green / orange / red / grey */}
          <div className="flex items-center gap-1 border border-gray-200 rounded-lg bg-white overflow-hidden">
            {([
              ['all', 'All'],
              ['blue', '🔵 Confirmed'],
              ['green', '🟢 High'],
              ['orange', '🟠 Deliverable'],
              ['red', '🔴 Bad'],
              ['grey', '⚪ Unchecked'],
            ] as const).map(([v, label]) => (
              <button key={v} onClick={() => setFilters(f => ({ ...f, verifyFilter: v }))}
                className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${filters.verifyFilter === v ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {showSicPicker && (
          <div className="border-t border-gray-100 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white flex-1 max-w-xs">
                <Search className="w-3 h-3 text-gray-400" />
                <input type="text" placeholder="Search SIC…" value={sicSearch} onChange={e => setSicSearch(e.target.value)}
                  className="outline-none text-[11px] text-gray-700 placeholder-gray-400 bg-transparent w-full" />
              </div>
              {filters.sicCodes.length > 0 && (
                <button onClick={() => setFilters(f => ({ ...f, sicCodes: [] }))} className="text-[11px] text-gray-400 hover:text-gray-600 flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto space-y-2.5">
              {Object.entries(filteredSics).map(([group, codes]) => (
                <div key={group}>
                  <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest mb-1">{group}</p>
                  <div className="flex flex-wrap gap-1">
                    {codes.map(sic => (
                      <button key={sic.code} onClick={() => toggleSic(sic.code)}
                        className={`text-[10px] px-2 py-0.5 rounded border font-medium transition-colors ${filters.sicCodes.includes(sic.code) ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-200 text-gray-600 bg-white hover:border-brand-400 hover:text-brand-600'}`}>
                        {sic.code} — {sic.label.length > 28 ? sic.label.slice(0, 28) + '…' : sic.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!showSicPicker && filters.sicCodes.length > 0 && (
          <div className="border-t border-gray-100 px-3 py-2 flex flex-wrap gap-1">
            {filters.sicCodes.map(code => {
              const sic = SIC_CODES.find(s => s.code === code)
              return (
                <span key={code} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-md">
                  {code}{sic ? ` — ${sic.label.slice(0, 20)}` : ''}
                  <button onClick={() => toggleSic(code)}><X className="w-2 h-2" /></button>
                </span>
              )
            })}
          </div>
        )}
      </div>

      {/* Track selector — New → then split into Companies / Directors */}
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
          {([
            ['new', 'New', 'Building2'],
            ['companies', 'Companies', 'Building2'],
            ['directors', 'Directors', 'UserPlus'],
          ] as const).map(([t, label]) => {
            const on = track === t
            const bg = t === 'companies' ? 'bg-indigo-600' : t === 'directors' ? 'bg-brand-600' : 'bg-gray-800'
            return (
              <button key={t} onClick={() => changeTrack(t)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold border-l first:border-l-0 border-gray-200 transition-colors ${on ? `${bg} text-white` : 'text-gray-600 hover:bg-gray-50'}`}>
                {t === 'directors' ? <UserPlus className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                {label}
                <span className={`text-[10px] ${on ? 'opacity-80' : 'text-gray-400'}`}>{trackCounts[t]}</span>
              </button>
            )
          })}
        </div>
        <p className="text-[11px] text-gray-400">
          {track === 'new' ? 'Synced companies — click Get Directors to start'
            : track === 'companies' ? 'Generic info@ inboxes → company campaigns'
            : 'Director personal emails → director campaigns'}
        </p>
      </div>

      {/* Stage sub-tabs — Companies + Directors tracks only (New has none) */}
      {track !== 'new' && (
      <div className="flex items-stretch gap-1.5 overflow-x-auto">
        {trackStages.map((s, i) => {
          const on = stage === s.key
          const accent = track === 'companies'
          return (
            <button key={s.key} onClick={() => changeStage(s.key)} title={s.hint}
              className={`flex-1 min-w-[140px] text-left rounded-lg border px-3 py-2 transition-colors ${
                on ? (accent ? 'border-indigo-500 bg-indigo-50' : 'border-brand-500 bg-brand-50') : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wide ${on ? (accent ? 'text-indigo-600' : 'text-brand-600') : 'text-gray-400'}`}>
                  {i < trackStages.length - 1 ? `${i + 1}. ` : ''}{s.label}
                </span>
                <span className={`text-xs font-bold ${on ? (accent ? 'text-indigo-700' : 'text-brand-700') : 'text-gray-500'}`}>{stageCounts[s.key] ?? 0}</span>
              </div>
              <p className="text-[9px] text-gray-400 mt-0.5 leading-tight">{s.hint}</p>
            </button>
          )
        })}
      </div>
      )}

      {/* Main table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-xs text-gray-400">Loading companies…</div>
        ) : !displayedRows.length ? (
          <div className="py-16 text-center">
            <Building2 className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-500 font-medium text-xs">No companies yet</p>
            <p className="text-gray-400 text-[11px] mt-1">Adjust filters and click <strong>Backfill from Companies House</strong></p>
          </div>
        ) : (
          <>
            {/* Bulk action toolbar — stage-aware */}
            {(() => {
              const selCount = track === 'new' ? selectedCompanies.size : track === 'companies' ? selectedInboxes.size : selected.size
              return (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex-wrap">
                  <button onClick={() => selectAll('page')}
                    className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 hover:text-brand-600 px-2.5 py-1 rounded border border-gray-200 bg-white hover:border-brand-400 transition-colors"
                    title="Select only the rows visible on this page">
                    <CheckSquare className="w-3 h-3" /> Select page ({
                      track === 'new' || track === 'companies' ? pagedRows.length : officersOnPage.length
                    })
                  </button>
                  {displayedRows.length > pagedRows.length && (
                    <button onClick={() => selectAll('all')}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-brand-700 hover:text-brand-900 px-2.5 py-1 rounded border border-brand-200 bg-brand-50 hover:bg-brand-100 transition-colors"
                      title="Select every row matching the current filters, across all pages">
                      <CheckSquare className="w-3 h-3" /> Select all matching ({
                        track === 'new' || track === 'companies' ? displayedRows.length : allOfficersFiltered.length
                      })
                    </button>
                  )}
                  {selCount > 0 && (
                    <button onClick={clearSelection}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 px-2.5 py-1 rounded border border-gray-200 bg-white transition-colors">
                      <Square className="w-3 h-3" /> Clear ({selCount})
                    </button>
                  )}
                  <div className="w-px h-4 bg-gray-200" />

                  {/* ── New track: get directors ── */}
                  {track === 'new' && (<>
                    <button onClick={getDirectorsForSelected} disabled={bulkProgress !== null || selectedCompanies.size === 0}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 px-3 py-1 rounded border border-brand-600 transition-colors disabled:opacity-40">
                      <UserPlus className="w-3 h-3" /> Get Directors → ({selectedCompanies.size})
                    </button>
                    <button onClick={() => refreshAll()} disabled={bulkProgress !== null}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 hover:text-brand-600 px-2.5 py-1 rounded border border-gray-200 bg-white hover:border-brand-400 transition-colors disabled:opacity-50">
                      <UserPlus className="w-3 h-3" /> Get All ({displayedRows.length})
                    </button>
                  </>)}

                  {/* ── Directors track actions ── */}
                  {track === 'directors' && (<>
                    {unverifiedOfficers.length > 0 && (
                      <button onClick={verifyDomains} disabled={bulkProgress !== null}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1 rounded border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                        title="Free MX check on emails not yet graded — no AI cost">
                        <CheckCircle2 className="w-3 h-3" /> Verify domains ({unverifiedOfficers.length})
                      </button>
                    )}
                    {deepVerifyableOfficers.length > 0 && (
                      <button onClick={verifyOnline} disabled={bulkProgress !== null}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-green-800 hover:text-green-900 px-2.5 py-1 rounded border border-green-300 bg-green-100 hover:bg-green-200 transition-colors disabled:opacity-50"
                        title="AI + web search to confirm the person exists online — costs money">
                        <Sparkles className="w-3 h-3" /> Deep verify ({deepVerifyableOfficers.length})
                      </button>
                    )}
                    {stage === 'directors' && selected.size > 0 && (
                      <button onClick={findEmailsForSelected} disabled={bulkProgress !== null}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-blue-800 hover:text-blue-900 px-2.5 py-1 rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 transition-colors disabled:opacity-50"
                        title="Web search for the director's actual published email. Costs money per lead and often finds nothing — a published address is only replaced when one is genuinely found.">
                        <Search className="w-3 h-3" /> Find real email ({selected.size})
                      </button>
                    )}
                    {stage === 'directors' && (
                      <button onClick={enrichSelected} disabled={bulkProgress !== null || selected.size === 0}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded border border-amber-600 transition-colors disabled:opacity-40">
                        <Zap className="w-3 h-3" /> Enrich → ({selected.size})
                      </button>
                    )}
                    {stage === 'master' && (
                      <button onClick={() => enrichAll()} disabled={bulkProgress !== null || enrichableOfficers.length === 0}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 hover:text-amber-600 px-2.5 py-1 rounded border border-gray-200 bg-white hover:border-amber-400 transition-colors disabled:opacity-50">
                        <Zap className="w-3 h-3" /> Enrich All ({enrichableOfficers.length})
                      </button>
                    )}
                    {stage !== 'directors' && (
                      <button onClick={openBulkModal} disabled={selected.size === 0}
                        className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 px-3 py-1 rounded border border-brand-600 transition-colors disabled:opacity-40">
                        <Link2 className="w-3 h-3" /> {stage === 'campaign' ? `Link to another (${selected.size})` : `Link → Director campaign (${selected.size})`}
                      </button>
                    )}
                  </>)}

                  {/* ── Companies track action ── */}
                  {track === 'companies' && (
                    <button onClick={openBulkModal} disabled={selectedInboxes.size === 0}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1 rounded border border-indigo-600 transition-colors disabled:opacity-40">
                      <Link2 className="w-3 h-3" />
                      {stage === 'campaign' ? `Link to another (${selectedInboxes.size})` : `Link → Company campaign (${selectedInboxes.size})`}
                    </button>
                  )}

                  <span className="ml-auto text-[11px] text-gray-400 font-medium">
                    {displayedRows.length.toLocaleString()} shown{track === 'directors' ? ` · ${allOfficersFiltered.length} directors` : track === 'companies' ? ' · company inboxes' : ' · new companies'}
                  </span>
                </div>
              )
            })()}

            {/* Table */}
            {paginationBar('top')}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-3 py-2 w-8" />
                    <SortTh col="company_name" label="Company" active={sortCol} dir={sortDir} onSort={sortToggle} />
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">No.</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">SIC</th>
                    <SortTh col="registered_locality" label="Location" active={sortCol} dir={sortDir} onSort={sortToggle} />
                    <SortTh col="incorporation_date" label="Incorp." active={sortCol} dir={sortDir} onSort={sortToggle} />
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Actions</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Campaigns</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((company, ci) => {
                    const officers = normaliseOfficers(company)
                    // Company-row checkbox only in the New track (select companies for Get Directors).
                    // Directors track = tick directors only; Companies track = tick the inbox line.
                    const showCompanyCheckbox = track === 'new'
                    const companyChecked = selectedCompanies.has(company.id)
                    const companyIndeterminate = false

                    return (
                      <CompanyBlock
                        key={company.id}
                        company={company}
                        officers={officers}
                        showCompanyCheckbox={showCompanyCheckbox}
                        companyChecked={companyChecked}
                        companyIndeterminate={companyIndeterminate}
                        selected={selected}
                        isFirst={ci === 0}
                        emailSelectMode={emailSelectMode}
                        addressView={addressView}
                        inbox={companyInbox(officers)}
                        inboxCampaigns={inboxCampaignMap?.get(company.id) ?? []}
                        inboxChecked={selectedInboxes.has(company.id)}
                        onToggleInbox={() => toggleInbox(company.id)}
                        onLinkInbox={() => openInboxModal(company.id)}
                        onToggleCompany={() => toggleCompany(company, officers)}
                        onToggleDirector={toggleDirector}
                        onGetDirectors={() => dirMut.mutate(company.id)}
                        dirLoading={dirMut.isPending && dirMut.variables === company.id}
                        dirResult={dirMut.isSuccess && dirMut.variables === company.id ? dirMut.data as { officers: number } : null}
                        onEnrich={id => enrichMut.mutate(id)}
                        enrichingId={enrichMut.isPending ? enrichMut.variables as string : null}
                        onLinkToCampaign={openCampaignModal}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>

            {paginationBar('bottom')}
          </>
        )}
      </div>

      {/* Add to Campaign modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  {modalTarget === 'company' ? <Building2 className="w-3.5 h-3.5 text-indigo-500" /> : <UserPlus className="w-3.5 h-3.5 text-brand-500" />}
                  Link to {modalTarget === 'company' ? 'Company' : 'Director'} campaign
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {modalTarget === 'company'
                    ? `${selectedInboxes.size} company inbox${selectedInboxes.size !== 1 ? 'es' : ''}`
                    : `${selected.size} director email${selected.size !== 1 ? 's' : ''}`} selected
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-4">
              {(() => {
                const visibleCampaigns = (campaigns ?? []).filter(c => c.audience === modalTarget)
                return (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{modalTarget === 'company' ? 'Company' : 'Director'} campaigns</p>
                {!userId ? (
                  <p className="text-xs text-gray-400">Authenticating…</p>
                ) : campaignsLoading ? (
                  <p className="text-xs text-gray-400">Loading campaigns…</p>
                ) : campaignsError ? (
                  <p className="text-xs text-red-500">Error loading campaigns — {(campaignsError as Error).message}</p>
                ) : !visibleCampaigns.length ? (
                  <p className="text-xs text-gray-400 italic">No {modalTarget} campaigns yet — create one below.</p>
                ) : (
                  <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                    {visibleCampaigns.map(c => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-300 cursor-pointer transition-colors">
                        <input type="checkbox"
                          checked={selectedCampaigns.has(c.id)}
                          onChange={() => setSelectedCampaigns(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n })}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                        <div>
                          <p className="text-xs font-medium text-gray-800">{c.name}</p>
                          {c.description && <p className="text-[10px] text-gray-400">{c.description}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
                )
              })()}

              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Or create new {modalTarget} campaign</p>
                <div className="flex gap-2">
                  <input type="text" placeholder={modalTarget === 'company' ? 'e.g. Company Intro…' : 'e.g. Weekly Digest…'} value={newCampaignName}
                    onChange={e => setNewCampaignName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && newCampaignName.trim() && createMut.mutate()}
                    className="input flex-1 text-xs py-2" />
                  <button onClick={() => createMut.mutate()}
                    disabled={!newCampaignName.trim() || createMut.isPending}
                    className="btn-secondary text-xs py-2 flex items-center gap-1 disabled:opacity-40">
                    <Plus className="w-3 h-3" />{createMut.isPending ? '…' : 'Create'}
                  </button>
                </div>
                {createMut.isError && (
                  <p className="mt-1.5 text-[11px] text-red-500">{(createMut.error as Error).message}</p>
                )}
              </div>

              {/* Segments — link the same leads directly into segments too */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">And / or link to segment(s)</p>
                {(segmentsList ?? []).length > 0 && (
                  <div className="space-y-1 max-h-36 overflow-y-auto pr-1 mb-2">
                    {(segmentsList ?? []).map(s => (
                      <label key={s.id} className="flex items-center gap-3 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-indigo-300 cursor-pointer transition-colors">
                        <input type="checkbox" checked={selectedSegments.has(s.id)}
                          onChange={() => setSelectedSegments(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n })}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-xs text-gray-700">{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="text" placeholder="New segment name…" value={newSegmentName}
                    onChange={e => setNewSegmentName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && newSegmentName.trim() && createSegMut.mutate()}
                    className="input flex-1 text-xs py-2" />
                  <button onClick={() => createSegMut.mutate()}
                    disabled={!newSegmentName.trim() || createSegMut.isPending}
                    className="btn-secondary text-xs py-2 flex items-center gap-1 disabled:opacity-40">
                    <Plus className="w-3 h-3" />{createSegMut.isPending ? '…' : 'Create'}
                  </button>
                </div>
              </div>

            </div>

            <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="btn-secondary text-xs">Cancel</button>
              <button onClick={() => addMut.mutate()}
                disabled={(selectedCampaigns.size === 0 && selectedSegments.size === 0) || addMut.isPending}
                className="btn-primary flex items-center gap-2 text-xs disabled:opacity-40">
                <Send className="w-3 h-3" />
                {addMut.isPending ? 'Adding…' : `Add to ${[
                  selectedCampaigns.size ? `${selectedCampaigns.size} campaign${selectedCampaigns.size !== 1 ? 's' : ''}` : '',
                  selectedSegments.size ? `${selectedSegments.size} segment${selectedSegments.size !== 1 ? 's' : ''}` : '',
                ].filter(Boolean).join(' + ') || '…'}`}
              </button>
            </div>
            {addMut.isError && (
              <p className="px-5 pb-4 text-[11px] text-red-500">{addMut.error instanceof Error ? addMut.error.message : 'Failed'}</p>
            )}
          </div>
        </div>
      )}

      {/* Backfill from Companies House modal */}
      {showBackfillModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Backfill from Companies House</h2>
                <p className="text-xs text-gray-500 mt-0.5">Import companies matching these filters. Existing ones are updated; new ones added.</p>
              </div>
              <button onClick={() => setShowBackfillModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Incorporated date range */}
              <div>
                <div className="flex items-center gap-1.5 text-gray-500 mb-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide">Incorporated</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {dateButtons.map(btn => (
                    <button key={btn.key} onClick={() => setFilters(f => ({ ...f, dateRange: btn.key }))}
                      className={`px-2.5 py-1 text-[11px] rounded-md font-medium border transition-colors ${filters.dateRange === btn.key ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-200 text-gray-600 hover:border-brand-400 hover:text-brand-600 bg-white'}`}>
                      {btn.label}
                    </button>
                  ))}
                </div>
                {filters.dateRange === 'custom' && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <input type="date" value={filters.customFrom} onChange={e => setFilters(f => ({ ...f, customFrom: e.target.value }))} className="input py-1 text-[11px]" />
                    <span className="text-gray-400 text-[11px]">to</span>
                    <input type="date" value={filters.customTo} onChange={e => setFilters(f => ({ ...f, customTo: e.target.value }))} className="input py-1 text-[11px]" />
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5">Tip: keep this to <strong>Yesterday</strong> or <strong>This week</strong> to import only newly-registered companies. The 06:00 cron already grabs each day's new arrivals.</p>
              </div>

              {/* Status / type / location / max */}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Status</span>
                  <select value={filters.companyStatus} onChange={e => setFilters(f => ({ ...f, companyStatus: e.target.value }))} className="input w-full py-1.5 text-[11px] mt-1">
                    <option value="active">Active</option>
                    <option value="">All statuses</option>
                    <option value="dissolved">Dissolved</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Type</span>
                  <select value={filters.companyType} onChange={e => setFilters(f => ({ ...f, companyType: e.target.value }))} className="input w-full py-1.5 text-[11px] mt-1">
                    <option value="ltd">Ltd</option>
                    <option value="">All types</option>
                    <option value="llp">LLP</option>
                    <option value="plc">PLC</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Location</span>
                  <input type="text" placeholder="City / postcode" value={filters.location}
                    onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
                    className="input w-full py-1.5 text-[11px] mt-1" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Max to import</span>
                  <select value={syncMax} onChange={e => setSyncMax(Number(e.target.value))} className="input w-full py-1.5 text-[11px] mt-1">
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                    <option value={2000}>2000</option>
                  </select>
                </label>
              </div>

              {/* SIC summary — set on the main view filter bar */}
              <div className="text-[11px] text-gray-500">
                <span className="font-semibold text-gray-600">SIC codes:</span>{' '}
                {filters.sicCodes.length
                  ? <span className="text-brand-600">{filters.sicCodes.length} selected (from the SIC filter)</span>
                  : <span className="text-gray-400 italic">none — importing all industries</span>}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowBackfillModal(false)} className="btn-secondary text-xs">Cancel</button>
              <button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}
                className="btn-primary flex items-center gap-2 text-xs disabled:opacity-40">
                <RefreshCw className={`w-3 h-3 ${syncMut.isPending ? 'animate-spin' : ''}`} />
                {syncMut.isPending ? 'Importing…' : 'Start backfill'}
              </button>
            </div>
            {syncMut.isError && (
              <p className="px-5 pb-4 text-[11px] text-red-500">{syncMut.error instanceof Error ? syncMut.error.message : 'Sync failed'}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── CompanyBlock ──────────────────────────────────────────────────────────────

function CompanyBlock({ company, officers, showCompanyCheckbox, companyChecked, companyIndeterminate, selected, isFirst, emailSelectMode, addressView, inbox, inboxCampaigns, inboxChecked, onToggleInbox, onLinkInbox, onToggleCompany, onToggleDirector, onGetDirectors, dirLoading, dirResult, onEnrich, enrichingId, onLinkToCampaign }: {
  company: CompanyData
  officers: OfficerData[]
  showCompanyCheckbox: boolean
  companyChecked: boolean
  companyIndeterminate: boolean
  selected: Set<string>
  isFirst: boolean
  emailSelectMode: boolean
  addressView: 'directors' | 'inboxes'
  inbox: string | null
  inboxCampaigns: { id: string; name: string }[]
  inboxChecked: boolean
  onToggleInbox: () => void
  onLinkInbox: () => void
  onToggleCompany: () => void
  onToggleDirector: (id: string) => void
  onGetDirectors: () => void
  dirLoading: boolean
  dirResult: { officers: number } | null
  onEnrich: (id: string) => void
  enrichingId: string | null
  onLinkToCampaign: (ids: string[]) => void
}) {
  const isActive = company.company_status === 'active'
  // Only deliverable (domain-verified) directors are campaign-eligible
  const verifiedIds = officers.filter(o => o.enriched?.domain_verified === true).map(o => o.id)

  return (
    <>
      {/* ── Company row — blue-left-border accent ── */}
      <tr className={`bg-white border-b border-gray-100 ${!isFirst ? 'border-t-2 border-t-gray-100' : ''}`}>
        <td className="pl-3 pr-1 py-2.5">
          {showCompanyCheckbox && (
            <input type="checkbox" checked={companyChecked}
              ref={el => { if (el) el.indeterminate = companyIndeterminate }}
              onChange={onToggleCompany}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
          )}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 rounded-full bg-brand-400 shrink-0" />
            <div>
              <p className="text-xs font-bold text-gray-900 leading-tight">{company.company_name}</p>
              {company.company_type && <p className="text-[9px] text-gray-400 uppercase tracking-wide">{company.company_type}</p>}
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 font-mono text-[10px] text-gray-500">{company.company_number}</td>
        <td className="px-3 py-2.5">
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
            {company.company_status ?? '—'}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex flex-wrap gap-0.5">
            {(company.sic_codes ?? []).slice(0, 2).map(code => (
              <span key={code} className="text-[9px] px-1 py-0.5 bg-gray-100 text-gray-500 rounded border border-gray-200">{code}</span>
            ))}
            {(company.sic_codes ?? []).length > 2 && <span className="text-[9px] text-gray-400">+{company.sic_codes.length - 2}</span>}
          </div>
        </td>
        <td className="px-3 py-2.5 text-[11px] text-gray-600 whitespace-nowrap">{company.registered_locality ?? '—'}</td>
        <td className="px-3 py-2.5 text-[11px] text-gray-600 whitespace-nowrap">
          {company.incorporation_date
            ? new Date(company.incorporation_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
            : '—'}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={onGetDirectors} disabled={dirLoading}
              className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:border-brand-400 hover:text-brand-600 disabled:opacity-50 transition-colors whitespace-nowrap">
              <UserPlus className="w-2.5 h-2.5" />
              {dirLoading ? 'Loading…' : officers.length > 0 ? 'Refresh' : 'Get Directors'}
            </button>
            {addressView === 'directors' && verifiedIds.length > 0 && (
              <button onClick={() => onLinkToCampaign(verifiedIds)}
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-brand-200 bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors whitespace-nowrap">
                <Link2 className="w-2.5 h-2.5" /> Link to Campaign ({verifiedIds.length})
              </button>
            )}
            {dirResult?.officers === 0 && (
              <p className="text-[9px] text-amber-500 leading-tight max-w-[120px]">No directors in CH yet — check in 24–48h</p>
            )}
          </div>
        </td>
        <td className="px-3 py-2.5" />
      </tr>

      {/* ── Company generic inbox line (info@) — shown in the Company inboxes view ── */}
      {addressView === 'inboxes' && inbox && (
        <tr className="bg-indigo-50/40 border-b border-indigo-100/60">
          <td className="pl-8 pr-1 py-1.5">
            <input type="checkbox" checked={inboxChecked} onChange={onToggleInbox}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          </td>
          <td className="px-3 py-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-px h-4 bg-indigo-200 shrink-0" />
              <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide">Company inbox</span>
            </div>
          </td>
          <td colSpan={5} className="px-3 py-1.5">
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-indigo-50 text-indigo-600 border-indigo-200">
              <Building2 className="w-2.5 h-2.5" />{inbox}<span className="opacity-70">· real domain</span>
            </span>
          </td>
          <td className="px-3 py-1.5 whitespace-nowrap">
            <button onClick={onLinkInbox}
              className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors">
              <Link2 className="w-2.5 h-2.5" /> Link to Campaign
            </button>
          </td>
          <td className="px-3 py-1.5">
            {inboxCampaigns.length > 0 ? (
              <div className="flex flex-wrap gap-0.5">
                {inboxCampaigns.map(c => (
                  <span key={c.id} className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full whitespace-nowrap">{c.name}</span>
                ))}
              </div>
            ) : <span className="text-[10px] text-gray-300">—</span>}
          </td>
        </tr>
      )}

      {/* ── Director rows — hidden in the Company inboxes view ── */}
      {addressView === 'inboxes' ? null : officers.length === 0 ? null : officers.map(officer => {
        const e = officer.enriched
        const isEnriching = enrichingId === officer.id
        const isSelected = selected.has(officer.id)
        // In email stages only deliverable personal emails are tickable for campaigns
        const canSelect = !emailSelectMode || e?.domain_verified === true

        return (
          <tr key={officer.id} className="bg-blue-50/30 border-b border-blue-100/60">
            <td className="pl-8 pr-1 py-1.5">
              {canSelect && (
                <input type="checkbox" checked={isSelected} onChange={() => onToggleDirector(officer.id)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              )}
            </td>
            {/* Name */}
            <td className="px-3 py-1.5">
              <div className="flex items-center gap-1.5">
                <div className="w-px h-4 bg-blue-200 shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-gray-700">{officer.full_name}</p>
                  <p className="text-[9px] text-blue-400 capitalize">{officer.role ?? 'Director'}</p>
                </div>
              </div>
            </td>
            {/* Contact info */}
            <td colSpan={4} className="px-3 py-1.5">
              <div className="flex flex-wrap items-center gap-2">
                {isEnriching ? (
                  <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
                    <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Enriching…
                  </span>
                ) : e?.email ? (
                  <>
                    {(() => {
                      const tier = leadTier(e)
                      if (tier === 'blue') return (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-blue-600 text-white border-blue-700">
                          <CheckCircle2 className="w-2.5 h-2.5" />{e.email}
                          <span className="opacity-80">· person confirmed online</span>
                        </span>
                      )
                      if (tier === 'green') return (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-emerald-50 text-emerald-700 border-emerald-300">
                          <CheckCircle2 className="w-2.5 h-2.5" />{e.email}
                          <span className="opacity-70">· real domain · mailbox unverified</span>
                        </span>
                      )
                      if (tier === 'orange') return (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-orange-50 text-orange-700 border-orange-300">
                          <CheckCircle2 className="w-2.5 h-2.5" />{e.email}
                          <span className="opacity-70">· real domain · mailbox unverified</span>
                        </span>
                      )
                      if (tier === 'red') return (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-red-50 text-red-600 border-red-300">
                          <AlertCircle className="w-2.5 h-2.5" />{e.email}
                          <span className="opacity-70">· domain has no mail server</span>
                        </span>
                      )
                      return (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-50 text-gray-500 border-gray-300">
                          <AlertCircle className="w-2.5 h-2.5" />{e.email}
                          <span className="opacity-70">· unchecked</span>
                        </span>
                      )
                    })()}
                    {/* Last "Find real email" run — so a searched-and-missed lead
                        is distinguishable from one never searched, and you don't
                        pay to search it twice. */}
                    {e.email_searched_at && (() => {
                      const when = new Date(e.email_searched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                      const found = e.email_search_result === 'found_personal' || e.email_search_result === 'found_generic'
                      return (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                            found ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                          title={found
                            ? 'A published address was found by web search'
                            : 'Web search ran and found no published address — the patterned address above was kept'}
                        >
                          {found
                            ? `web search: found ${e.email_search_result === 'found_generic' ? 'generic' : 'personal'}`
                            : 'web search: none found'} · {when}
                        </span>
                      )
                    })()}
                    {e.company_website && (
                      <a href={e.company_website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                        <Globe className="w-2.5 h-2.5" /> Web
                      </a>
                    )}
                    {e.linkedin_url && (
                      <a href={e.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-700 hover:underline flex items-center gap-0.5">
                        <Linkedin className="w-2.5 h-2.5" /> LinkedIn
                      </a>
                    )}
                    {e.twitter_url && (
                      <a href={e.twitter_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline font-bold">𝕏</a>
                    )}
                  </>
                ) : (
                  // No email. Say WHY and WHEN — an empty cell reads as "broken"
                  // when the honest answer is usually "this company has no website".
                  (() => {
                    const when = e?.enriched_at
                      ? new Date(e.enriched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                      : null
                    if (e?.enrichment_status === 'failed') return (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-red-50 text-red-600 border-red-300">
                        <AlertCircle className="w-2.5 h-2.5" /> Enrich failed
                        {when && <span className="opacity-70">· {when}</span>}
                      </span>
                    )
                    if (e?.enrichment_status === 'running') return (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-amber-50 text-amber-700 border-amber-300">
                        <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Enriching…
                      </span>
                    )
                    if (e?.email_source_detail === 'no_domain_found') return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-600 border-gray-300"
                        title="Searched the web and found no website for this company — common for very recently incorporated firms. No email was invented."
                      >
                        <Search className="w-2.5 h-2.5" /> No website found
                        {when && <span className="opacity-70">· searched {when}</span>}
                      </span>
                    )
                    return <span className="text-[10px] text-gray-400 italic">Not enriched</span>
                  })()
                )}
              </div>
            </td>
            {/* Director actions */}
            <td className="px-3 py-1.5 whitespace-nowrap">
              <div className="flex items-center gap-1">
                <button onClick={() => onEnrich(officer.id)} disabled={isEnriching || e?.enrichment_status === 'running'}
                  className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-600 hover:border-amber-400 hover:text-amber-600 disabled:opacity-50 transition-colors">
                  <Sparkles className="w-2.5 h-2.5" />
                  {e?.email ? 'Re-enrich' : 'Enrich'}
                </button>
                {e?.domain_verified === true ? (
                  <button onClick={() => onLinkToCampaign([officer.id])}
                    className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border border-brand-200 bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors">
                    <Link2 className="w-2.5 h-2.5" /> Link to Campaign
                  </button>
                ) : e?.email ? (
                  <span className="text-[9px] text-orange-500 italic whitespace-nowrap">verify domain first</span>
                ) : null}
              </div>
            </td>
            {/* Campaigns — dedicated last column */}
            <td className="px-3 py-1.5">
              {officer.campaigns.length > 0 ? (
                <div className="flex flex-wrap gap-0.5">
                  {officer.campaigns.map(c => (
                    <span key={c.id} className="text-[9px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full whitespace-nowrap">{c.name}</span>
                  ))}
                </div>
              ) : (
                <span className="text-[10px] text-gray-300">—</span>
              )}
            </td>
          </tr>
        )
      })}
    </>
  )
}

// ── SortTh ────────────────────────────────────────────────────────────────────

function SortTh({ col, label, active, dir, onSort }: {
  col: SortCol; label: string; active: SortCol; dir: SortDir; onSort: (c: SortCol) => void
}) {
  const on = active === col
  return (
    <th className="px-3 py-2">
      <button onClick={() => onSort(col)}
        className={`flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors whitespace-nowrap ${on ? 'text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}>
        {label} <span className="text-[9px]">{on ? (dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </button>
    </th>
  )
}
